"use client";
import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import "mapbox-gl/dist/mapbox-gl.css";
import Map, { Marker, NavigationControl, Source, Layer } from "react-map-gl";
import type { MapRef } from "react-map-gl";
import Supercluster from "supercluster";
import Pin from "./pin";

interface ContactMapProps {
  contacts: any;
  contactLists: any;
}

const ContactMap: FC<ContactMapProps> = ({ contacts, contactLists }) => {
  const navigate = useNavigate();
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const mapRef = useRef<MapRef>(null);
  const [zoom, setZoom] = useState(10); // Store current zoom level

  // Convert contacts into GeoJSON data
  const geoJsonData = useMemo(
    () => ({
      type: "FeatureCollection",
      features: contacts.map((contact: any) => ({
        type: "Feature",
        properties: {
          cluster: false,
          contactId: contact.ID,
        },
        geometry: {
          type: "Point",
          coordinates: [Number(contact.longitude), Number(contact.latitude)],
        },
      })),
    }),
    [contacts],
  );

  // Initialize Supercluster
  const supercluster = useMemo(
    () =>
      new Supercluster({
        radius: 50, // Cluster radius
        maxZoom: 12, // Clustering stops at zoom 12
      }),
    [],
  );

  supercluster.load(geoJsonData.features);

  // Get clusters based on zoom level
  const clusters = useMemo(
    () => supercluster.getClusters([-180, -85, 180, 85], zoom),
    [zoom, supercluster],
  );

  const fitBounds = useCallback(() => {
    if (contacts.length === 0 || !mapRef.current) return;

    let minLng = Infinity,
      minLat = Infinity;
    let maxLng = -Infinity,
      maxLat = -Infinity;

    contacts.forEach((contact: any) => {
      if (contact?.latitude && contact?.longitude) {
        const lat = Number(contact.latitude);
        const lng = Number(contact.longitude);
        if (lat < minLat) minLat = lat;
        if (lng < minLng) minLng = lng;
        if (lat > maxLat) maxLat = lat;
        if (lng > maxLng) maxLng = lng;
      }
    });

    if (minLng !== Infinity && maxLng !== -Infinity) {
      mapRef.current.fitBounds(
        [
          [minLng, minLat], // Southwestern corner
          [maxLng, maxLat], // Northeastern corner
        ],
        { padding: 100, maxZoom: 20 },
      );
    }
  }, [contacts]);

  useEffect(() => {
    setPopupInfo(null);
    setTimeout(() => {
      fitBounds();
    }, 500);
  }, [contacts, fitBounds]);

  const isMobile = () => window.innerWidth <= 768;
  const [isMobileDevice, setIsMobileDevice] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(isMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Map
        ref={mapRef}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY}
        initialViewState={{
          longitude: 4.4051,
          latitude: 51.2213,
          zoom: 10,
        }}
        style={{
          width: "100%",
          height: isMobileDevice ? 400 : 600,
          borderRadius: "0",
          overflowY: "scroll",
        }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        onClick={() => setPopupInfo(null)}
        onZoom={(e) => setZoom(e.viewState.zoom)} // Update zoom level dynamically
      >
        {/* Cluster Source */}
        <Source
          id="clusters"
          type="geojson"
          data={{ type: "FeatureCollection", features: clusters }}
        >
          {/* Cluster Layer (Only render if it’s a cluster) */}
          <Layer
            id="cluster-layer"
            type="circle"
            filter={["has", "point_count"]} // Only applies to clusters
            paint={{
              "circle-color": "#000000",
              "circle-radius": 20,
              "circle-stroke-width": 2,
              "circle-stroke-color": "#fff",
            }}
          />
          {/* Cluster Count Layer (Number inside the circle) */}
          <Layer
            id="cluster-count"
            type="symbol"
            filter={["has", "point_count"]}
            layout={{
              "text-field": ["get", "point_count_abbreviated"],
              "text-size": 14,
              "text-anchor": "center",
            }}
          />
        </Source>

        {/* Render Individual Markers (Only if NOT in a cluster) */}
        {clusters.map((feature: any, index: number) => {
          const [longitude, latitude] = feature.geometry.coordinates;
          const { cluster, point_count } = feature.properties;

          // If it's a cluster, render a click-to-zoom marker
          if (cluster) {
            return (
              <Marker
                key={`cluster-${index}`}
                longitude={longitude}
                latitude={latitude}
                onClick={(e) => {
                  const expansionZoom = Math.min(
                    supercluster.getClusterExpansionZoom(feature.id),
                    16,
                  );
                  mapRef.current?.flyTo({
                    center: [longitude, latitude],
                    zoom: expansionZoom,
                    duration: 500,
                  });
                  e.originalEvent.stopPropagation(); // Prevent map click
                }}
              >
                <div className="relative flex items-center justify-center w-12 h-12 text-white bg-transparent rounded-full bg-opacity-80">
                  <div
                    className="absolute w-full h-full rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 80%)",
                    }}
                  />
                  <span className="relative text-sm font-semibold text-white">
                    {point_count}{" "}
                  </span>
                </div>
              </Marker>
            );
          }

          // If it's an individual point, render the Pin
          const contact = contacts.find(
            (contact: any) => contact.ID === feature.properties.contactId,
          );

          return (
            <Marker
              key={`marker-${index}`}
              longitude={longitude}
              latitude={latitude}
              anchor="bottom"
              onClick={(e) => {
                setPopupInfo(contact); // Set the correct contact info when clicked
                e.originalEvent.stopPropagation(); // Prevent map click
              }}
            >
              <Pin size={25} />
            </Marker>
          );
        })}

        {/* Popup Information (Keep it the same as requested) */}
        {popupInfo && (
          <div className="absolute top-5 left-5 bg-white p-3  overflow-y-scroll max-h-[400px] w-[250px] md:w-[300px] ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              className="absolute top-0 right-0 z-10 w-8 h-8 md:hidden stroke-white fill-red-600"
              onClick={() => setPopupInfo(null)}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <div className="relative w-20 w-full h-20 mb-2 overflow-hidden">
              {popupInfo.base64 ? (
                <img
                  src={`data:image/jpeg;base64,${popupInfo.base64}`}
                  alt={popupInfo.firstname + " " + popupInfo.lastname}
                  className="object-contain object-left w-full h-full"
                />
              ) : (
                <img
                  src={`../contact-image-placeholder.jpg`}
                  alt={popupInfo.firstname + " " + popupInfo.lastname}
                  className="object-contain object-left w-full h-full"
                />
              )}
            </div>

            <h4 className="flex gap-2 mb-2 ">
              <span className="text-xl font-normal text-default">
                {popupInfo?.company}
              </span>
            </h4>
            <p className="text-sm max-h-[95px] overflow-hidden pb-3 mb-1">
              <span>{popupInfo?.street}</span> <span>{popupInfo?.number}</span>
              <br /> <span>{popupInfo?.city}</span>{" "}
              <span>{popupInfo?.postcode}</span>
            </p>
            <p className="text-sm max-h-[95px] overflow-hidden pb-3 mb-1">
              <span className="mt-2">{popupInfo?.phone}</span>
              <br />
              <a href={`mailto${popupInfo?.email}`}> {popupInfo?.email}</a>
            </p>

            <div className="flex flex-wrap gap-2 mb-3 ">
              {popupInfo?.contact_list &&
                JSON.parse(popupInfo.contact_list).map(
                  (item: any, index: number) => {
                    const contact = contactLists.find(
                      (contact: any) => contact.ID === item,
                    );

                    return (
                      <p className="m-0" key={`contact-list-${index}`}>
                        {contact ? contact.name : "Unknown brand"}
                      </p>
                    );
                  },
                )}
            </div>

            <button
              onClick={() => navigate(`/contacts/${popupInfo?.ID}`)}
              className={`bg-gray-200 w-full text-sm inline-block text-center hover:text-white  transition-all  font-semibold rounded mt-2 p-2 transition-all`}
            >
              View details
            </button>
          </div>
        )}

        <NavigationControl />
      </Map>
    </>
  );
};

export default ContactMap;
